// Declarative registry of every CMS-managed content type. Drives the
// generic ContentList / ContentEdit pages so each of the 12 modules below
// doesn't need its own hand-built list/form screen.
export const MODULES = {
  navigation: {
    key: "navigation",
    label: "Navbar",
    description: "Header navigation links and the primary CTA button.",
    singleton: false,
    hasImage: false,
    apiPath: "/navigation",
    idField: "id",
    listColumns: ["label", "url", "is_cta"],
    fields: [
      { name: "label", label: "Label", type: "text", required: true },
      { name: "url", label: "Link (e.g. /technology)", type: "text", required: true },
      { name: "is_cta", label: "Primary CTA Button", type: "checkbox" },
    ],
  },
  home: {
    key: "home",
    label: "Home Page",
    description: "Homepage hero content shown above the fold.",
    singleton: true,
    hasImage: true,
    imageField: "image_url",
    imageLabel: "Hero Image",
    apiPath: "/home",
    fields: [
      { name: "mission_label", label: "Mission Label", type: "text", required: true },
      { name: "mission_title", label: "Mission Title", type: "text", required: true },
      { name: "mission_description", label: "Mission Description", type: "textarea", required: true },
    ],
  },
  technology: {
    key: "technology",
    label: "Technology / Specialties",
    description: "Core Specialties shown on the Technology hub.",
    singleton: false,
    hasImage: false,
    apiPath: "/technology",
    idField: "slug",
    listColumns: ["slug", "name"],
    fields: [
      { name: "slug", label: "Slug", type: "text", required: true },
      { name: "name", label: "Name", type: "text", required: true },
      { name: "short_description", label: "Short Description", type: "textarea", required: true },
      { name: "long_description", label: "Long Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: ["flight-proven", "in-development", "roadmap"] },
      { name: "capabilities", label: "Capabilities (JSON list)", type: "textarea" },
      { name: "related_products", label: "Related Products (JSON list)", type: "textarea" },
      { name: "mission_tag", label: "Mission Tag", type: "text" },
    ],
  },
  products: {
    key: "products",
    label: "Products & Platforms",
    description: "Named platforms shown on the Products hub.",
    singleton: false,
    hasImage: true,
    imageField: "image_url",
    imageLabel: "Platform Image",
    apiPath: "/products",
    idField: "slug",
    listColumns: ["slug", "name", "status"],
    fields: [
      { name: "slug", label: "Slug", type: "text", required: true },
      { name: "name", label: "Name", type: "text", required: true },
      { name: "one_line_pitch", label: "One-line Pitch", type: "text", required: true },
      { name: "status", label: "Status", type: "select", options: ["flight-proven", "in-development", "roadmap"], required: true },
      { name: "capability_blocks", label: "Capability Blocks (JSON)", type: "textarea" },
      { name: "spec_sheet_url", label: "Spec Sheet URL", type: "text" },
      { name: "is_export_controlled", label: "Export Controlled", type: "checkbox" },
      { name: "spec_summary", label: "Spec Summary", type: "textarea" },
    ],
  },
  solutions: {
    key: "solutions",
    label: "Solutions by Audience",
    description: "Persona pages shown on the Solutions hub.",
    singleton: false,
    hasImage: true,
    imageField: "image_url",
    imageLabel: "Persona Image",
    apiPath: "/solutions",
    idField: "slug",
    listColumns: ["slug", "name"],
    fields: [
      { name: "slug", label: "Persona Slug", type: "select", options: ["space-agencies", "commercial", "defense", "research"], required: true },
      { name: "name", label: "Name", type: "text", required: true },
      { name: "narrative", label: "Narrative", type: "textarea", required: true },
      { name: "engagement_paths", label: "Engagement Paths (JSON)", type: "textarea" },
    ],
  },
  missions: {
    key: "missions",
    label: "Case Studies & Missions",
    description: "Mission stories shown on the Case Studies hub.",
    singleton: false,
    hasImage: true,
    imageField: "image_url",
    imageLabel: "Mission Image",
    apiPath: "/missions",
    idField: "slug",
    adminScoped: true,
    listColumns: ["slug", "title", "reference_rights_approved"],
    fields: [
      { name: "slug", label: "Slug", type: "text", required: true },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "summary", label: "Summary", type: "textarea", required: true },
      { name: "challenge", label: "Challenge", type: "textarea" },
      { name: "approach", label: "Approach", type: "textarea" },
      { name: "technology_used", label: "Technology Used", type: "textarea" },
      { name: "outcome", label: "Outcome", type: "textarea" },
      { name: "reference_rights_approved", label: "Approved for Public Listing", type: "checkbox" },
    ],
  },
  newsroom: {
    key: "newsroom",
    label: "Newsroom & Press",
    description: "Press releases and external coverage.",
    singleton: false,
    hasImage: false,
    apiPath: "/newsroom",
    idField: "slug",
    listColumns: ["slug", "title", "published_date"],
    fields: [
      { name: "slug", label: "Slug", type: "text", required: true },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "body", label: "Body", type: "textarea" },
      { name: "is_external", label: "External Coverage", type: "checkbox" },
      { name: "source_outlet", label: "Source Outlet", type: "text" },
      { name: "published_date", label: "Published Date", type: "date", required: true },
    ],
  },
  careers: {
    key: "careers",
    label: "Careers",
    description: "Open job requisitions.",
    singleton: false,
    hasImage: false,
    apiPath: "/careers",
    idField: "slug",
    adminScoped: true,
    listColumns: ["slug", "title", "discipline", "is_open"],
    fields: [
      { name: "slug", label: "Slug", type: "text", required: true },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "discipline", label: "Discipline", type: "text", required: true },
      { name: "location", label: "Location", type: "text" },
      { name: "requires_clearance", label: "Requires Clearance", type: "checkbox" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "is_open", label: "Open", type: "checkbox" },
    ],
  },
  library: {
    key: "library",
    label: "Technical Library",
    description: "Whitepapers, spec sheets, and briefings.",
    singleton: false,
    hasImage: false,
    apiPath: "/library",
    idField: "slug",
    listColumns: ["slug", "title", "asset_type", "published_date"],
    fields: [
      { name: "slug", label: "Slug", type: "text", required: true },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "asset_type", label: "Asset Type", type: "select", options: ["whitepaper", "spec_sheet", "briefing", "case_study"], required: true },
      { name: "specialty_tag", label: "Specialty Tag", type: "text" },
      { name: "persona_tag", label: "Persona Tag", type: "text" },
      { name: "is_gated", label: "Gated", type: "checkbox" },
      { name: "file_url", label: "File URL", type: "text" },
      { name: "published_date", label: "Published Date", type: "date", required: true },
    ],
  },
  about: {
    key: "about",
    label: "About Us",
    description: "Company mission, vision, and heritage.",
    singleton: true,
    hasImage: false,
    apiPath: "/about",
    fields: [
      { name: "mission_statement", label: "Mission Statement", type: "textarea", required: true },
      { name: "vision_statement", label: "Vision Statement", type: "textarea", required: true },
      { name: "leadership", label: "Leadership (JSON)", type: "textarea" },
      { name: "heritage_timeline", label: "Heritage Timeline (JSON)", type: "textarea" },
      { name: "locations", label: "Locations (JSON)", type: "textarea" },
    ],
  },
  investors: {
    key: "investors",
    label: "Investors & Partners",
    description: "Company snapshot and partner program.",
    singleton: true,
    hasImage: false,
    apiPath: "/investors",
    fields: [
      { name: "company_snapshot", label: "Company Snapshot", type: "textarea", required: true },
      { name: "partner_program", label: "Partner Program", type: "textarea", required: true },
      { name: "disclosures", label: "Disclosures", type: "textarea" },
    ],
  },
  research: {
    key: "research",
    label: "Research & Innovation",
    description: "Research division overview.",
    singleton: true,
    hasImage: false,
    apiPath: "/research",
    fields: [
      { name: "overview", label: "Overview", type: "textarea", required: true },
      { name: "partnership_program", label: "Partnership Program", type: "textarea" },
    ],
  },
  trust: {
    key: "trust",
    label: "Trust & Compliance",
    description: "Export control posture and certifications.",
    singleton: true,
    hasImage: false,
    apiPath: "/trust",
    fields: [
      { name: "export_control_posture", label: "Export Control Posture", type: "textarea", required: true },
      { name: "gated_content_policy", label: "Gated Content Policy", type: "textarea", required: true },
      { name: "data_privacy_practices", label: "Data Privacy Practices", type: "textarea", required: true },
      { name: "responsible_disclosure_email", label: "Responsible Disclosure Email", type: "text", required: true },
      { name: "certifications", label: "Certifications (JSON)", type: "textarea" },
    ],
  },
  missionConsultations: {
    key: "missionConsultations",
    label: "Mission Consultations",
    description: "Mission Consultation requests submitted from the public site.",
    singleton: false,
    hasImage: false,
    readOnly: true,
    apiPath: "/contact",
    idField: "id",
    listColumns: ["name", "organization", "email", "created_at"],
    fields: [
      { name: "name", label: "Full Name", type: "text" },
      { name: "organization", label: "Organization", type: "text" },
      { name: "role", label: "Role / Title", type: "text" },
      { name: "email", label: "Email", type: "text" },
      { name: "country", label: "Country", type: "text" },
      { name: "persona", label: "Organization Type", type: "text" },
      { name: "mission_phase", label: "Mission Phase", type: "text" },
      { name: "target_orbit_or_destination", label: "Target Orbit / Destination", type: "text" },
      { name: "timeframe", label: "Timeframe", type: "text" },
      { name: "rfp_document_url", label: "RFP Document URL", type: "text" },
      { name: "consent_given", label: "Consent Given", type: "checkbox" },
      { name: "created_at", label: "Submitted At", type: "text" },
    ],
  },
};

export const MODULE_LIST = Object.values(MODULES);

export function emptyRecord(module) {
  const record = {};
  module.fields.forEach((f) => {
    record[f.name] = f.type === "checkbox" ? false : "";
  });
  if (module.hasImage) record[module.imageField] = null;
  return record;
}
